import * as Sentry from '@sentry/react'

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { Endpoint, RoleMimetype, getApiUrl } from 'constants/API'
import { auth0ClientModule, clearAuth0LocalStorage, getRoles } from 'utils/auth'
import { isEmbedRoute } from 'constants/router'
import { embedSession } from 'utils/embedSession'

import { APIErrorDTO } from 'models/API'
import { Auth0Roles } from 'models/auth'

/** Header names that must never be shipped to Sentry (case-insensitive). */
const SENSITIVE_HEADERS = ['authorization', 'cookie', 'set-cookie', 'x-api-key', 'proxy-authorization']

/** Returns a shallow copy of the headers with sensitive values redacted. */
export const _redactHeaders = (headers?: Record<string, unknown>): Record<string, unknown> | undefined => {
  if (!headers || typeof headers !== 'object') return undefined
  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      SENSITIVE_HEADERS.includes(key.toLowerCase()) ? '[REDACTED]' : value,
    ])
  )
}

/**
 * Strips the query string and fragment from a URL, keeping only the path.
 * Query params can carry secrets/PII (e.g. `?token=`, `?email=`) that must not
 * reach Sentry, so only the path is ever reported.
 */
export const _stripUrlQuery = (url?: string): string | undefined => url?.split(/[?#]/)[0]

/**
 * Whitelists only the non-sensitive fields of an API error response body.
 * Returns `undefined` when the body is absent or not a structured error object
 * (e.g. an HTML error page), so arbitrary bodies / PII are never attached.
 */
export const _safeResponseData = (data: unknown): Pick<APIErrorDTO, 'error' | 'path' | 'status' | 'timestamp'> | undefined => {
  if (!data || typeof data !== 'object') return undefined
  const { error, path, status, timestamp } = data as APIErrorDTO
  if (error === undefined && path === undefined && status === undefined && timestamp === undefined) return undefined
  return { error, path, status, timestamp }
}

/**
 * Builds a safe, non-sensitive metadata object from an Axios error for Sentry.
 * Whitelists only request/response metadata — never the raw Axios error, request,
 * response, params, or bodies, which can carry auth tokens and PII. URLs are
 * query-stripped and the response body is reduced to non-sensitive fields.
 */
export const _buildSafeErrorContext = (error: AxiosError) => ({
  method: error.config?.method?.toUpperCase(),
  url: _stripUrlQuery(error.config?.url),
  baseURL: _stripUrlQuery(error.config?.baseURL),
  status: error.response?.status,
  statusText: error.response?.statusText,
  code: error.code,
  message: error.message,
  requestHeaders: _redactHeaders(error.config?.headers as Record<string, unknown> | undefined),
  responseHeaders: _redactHeaders(error.response?.headers as Record<string, unknown> | undefined),
  responseError: _safeResponseData(error.response?.data),
})

export interface APIRequestConfig {
  /** Set to true if error should be ignored by Sentry */
  ignoreError?: boolean
  /** A method returning boolean value, if true returned error will be ignored by Sentry */
  ignoreErrorFactory?: (error: AxiosError) => boolean
  /** Endpoint string logged to Sentry */
  endpoint?: string
  /** If set to true, base API URL will be ignored */
  ignoreBaseUrl?: boolean
  /** If set to true, authorization headers will be ignored */
  ignoreAuthorizationHeaders?: boolean
}

/** API negotiator */
export class API {
  /** API baseURL to which all enpoints are appended */
  baseUrl: string

  /** Creates an instance of API. */
  constructor(baseURL: string) {
    this.baseUrl = baseURL
  }

  /** Calls auth0 getTokenSilently() asynchronously */
  private async getToken() {
    // Propstack embed (ISSUE-2338): the broker session replaces the Auth0 SPA session entirely.
    if (embedSession.isActive()) {
      return await embedSession.getAccessToken() ?? ''
    }
    // Embed surfaces never create the SPA client (ISSUE-3180): without a live broker session,
    // send no credential and let the 401 surface — the fallback below would wipe & reload.
    if (isEmbedRoute()) return ''
    try {
      return await auth0ClientModule.getTokenSilently()
    } catch (e) {
      clearAuth0LocalStorage()
      return ''
    }
  }


  /**
   * Calls internally this.getToken() asynchronously and sets up the authorization headers object
   * @returns object: {Authorization: `Bearer ${token}`}
   */
  private async getAuthorizationHeaders() {
    return {
      Authorization: `Bearer ${await this.getToken()}`
    }
  }

  /** Returns the role mimetype based on auth0 roles */
  public decideRoleMimeType(roles: Auth0Roles) {
    if (roles.isAdmin) return RoleMimetype.ADMIN
    if (roles.isCreative) return RoleMimetype.CREATIVE
    if (roles.isClient) return RoleMimetype.CLIENT

    return RoleMimetype.DEFAULT
  }

  /**
   * Returns the **v2** role mimetype based on auth0 roles. Mirrors
   * `decideRoleMimeType` but resolves the `*.v2+json` variants used by the
   * PF v2 (revamp) session-bound endpoints. Callers pass the result as both
   * `Accept` and `Content-Type` (via the `options.headers` override on
   * `useAPI`) to opt the request into the v2 data model.
   */
  public decideRoleMimeTypeV2(roles: Auth0Roles) {
    // Only two v2 models exist: the platform's `Constants.V2` defines client,
    // admin and service, and the PF v2 endpoints produce client + admin. Asking
    // for anything else is a 406 the flow shows as "we couldn't load the
    // catalogue" — which is what a creative-roled account used to get, since
    // `isCreative` was tested before `isClient` and CREATIVE_V2 has no producer
    // anywhere. Everyone who is not an admin therefore asks for the client
    // model; a caller who may not have it is refused by the endpoint's own
    // CLIENT access rule, which is an honest 403 rather than a mimetype puzzle.
    if (roles.isAdmin) return RoleMimetype.ADMIN_V2

    return RoleMimetype.CLIENT_V2
  }

  /** Calls auth0 getUser() asynchronously and returns the role mimetype based on auth0 roles */
  public async getRoleMimeType() {
    // Embed surfaces are always CLIENT — there is no Auth0 user to derive roles from.
    if (embedSession.isActive() || isEmbedRoute()) return RoleMimetype.CLIENT
    const roles = await getRoles()
    return this.decideRoleMimeType(roles)
  }

  /**
   * Async counterpart of `decideRoleMimeTypeV2`, for callers outside a React
   * render where `useAuth0().roles` is unavailable — the PF v2 file-hub upload
   * resolvers, which run inside a FileAPI callback rather than a component.
   */
  public async getRoleMimeTypeV2() {
    // Embed broker sessions are always CLIENT — there is no Auth0 user to derive roles from.
    if (embedSession.isActive()) return RoleMimetype.CLIENT_V2
    const roles = await getRoles()
    return this.decideRoleMimeTypeV2(roles)
  }

  /**
   * Calls internally this.getRoleMimeType() asynchronously and sets up the Accept headers object with role mimetype
   * @returns object: {Accept: `application/{role}`}
   */
  private async geContentTypeRoleHeaders() {
    return {
      Accept: `${await this.getRoleMimeType()}`
    }
  }

  /** Combines argument axios config with authorization headers */
  private async createConfigWithAuthorization(config?: AxiosRequestConfig): Promise<AxiosRequestConfig> {
    const authorizationHeaders = await this.getAuthorizationHeaders()
    const contentTypeRoleHeaders = await this.geContentTypeRoleHeaders()

    return {
      ...config,
      headers: {
        ...contentTypeRoleHeaders,
        ...config?.headers,
        ...authorizationHeaders,
      },
    }
  }

  /** Logs the axios error to Sentry */
  public logError<T>(error: AxiosError<T>, internalConfig?: APIRequestConfig) {
    if (internalConfig?.ignoreError) return
    if (internalConfig?.ignoreErrorFactory && internalConfig?.ignoreErrorFactory(error)) return
    // 401s are expected (expired/missing token, not-yet-authenticated) and are
    // handled by the auth flow — they are noise in Sentry, so skip reporting them.
    if (error.response?.status === 401) return

    let message = ''
    if (error.config?.method) message += `${error.config.method.toUpperCase()}`
    if (internalConfig?.endpoint) message += ` - ${internalConfig.endpoint}`
    else if (error.config?.url) message += ` - ${_stripUrlQuery(error.config.url)}`
    if (error.response?.status) message += ` - ${error.response.status}`
    if (error.response?.data) {
      const data: APIErrorDTO = error.response.data
      if (data.message) message += ` - ${data.message}`
    }
    Sentry.captureMessage(message, scope => {
      scope.clearBreadcrumbs()
      scope.setFingerprint([message])
      // Only attach whitelisted, redacted metadata — never the raw Axios error,
      // request, response, params or bodies, which carry the Authorization
      // bearer token and potential PII (ISSUE-2094).
      scope.setExtra('AxiosErrorContext', _buildSafeErrorContext(error))
      return scope
    })
  }

  /** Processes the request and logs erroneous responses */
  private async processRequest<T>(request: Promise<AxiosResponse<T>>, internalConfig?: APIRequestConfig) {
    try {
      return await request
    } catch (e) {
      const error = e as AxiosError<T>
      if (error.isAxiosError) this.logError(error, internalConfig)
      throw e
    }
  }

  /** Creates an API GET endpoint call factory */
  public async createGetFactory<T>(endpoint: Endpoint | string, config?: AxiosRequestConfig, internalConfig?: APIRequestConfig) {
    return async () => axios.get<T>(`${internalConfig?.ignoreBaseUrl ? '' : this.baseUrl}${endpoint}`, internalConfig?.ignoreAuthorizationHeaders ? (config ?? {}) : await this.createConfigWithAuthorization(config))
  }

  /** Calls an API endpoint (appending it to the baseURL) with authorization headers */
  public async get<T>(endpoint: Endpoint | string, config?: AxiosRequestConfig, internalConfig?: APIRequestConfig) {
    return this.processRequest(
      (await this.createGetFactory<T>(endpoint, config, internalConfig))(),
      internalConfig
    )
  }

  /** Creates an API POST endpoint call factory */
  public async createPostFactory<T>(endpoint: Endpoint | string, body: any, config?: AxiosRequestConfig, internalConfig?: APIRequestConfig) {
    return async () => axios.post<T>(`${internalConfig?.ignoreBaseUrl ? '' : this.baseUrl}${endpoint}`, body, internalConfig?.ignoreAuthorizationHeaders ? (config ?? {}) : await this.createConfigWithAuthorization(config))
  }

  /** Calls an API endpoint (appending it to the baseURL) with authorization headers */
  public async post<T>(endpoint: Endpoint | string, body: any, config?: AxiosRequestConfig, internalConfig?: APIRequestConfig) {
    return this.processRequest(
      (await this.createPostFactory<T>(endpoint, body, config, internalConfig))(),
      internalConfig
    )
  }

  /** Creates an API PATCH endpoint call factory */
  public async createPatchFactory<T>(endpoint: Endpoint | string, body: any, config?: AxiosRequestConfig, internalConfig?: APIRequestConfig) {
    return async () => axios.patch<T>(`${internalConfig?.ignoreBaseUrl ? '' : this.baseUrl}${endpoint}`, body, internalConfig?.ignoreAuthorizationHeaders ? (config ?? {}) : await this.createConfigWithAuthorization(config))
  }

  /** Calls an API endpoint (appending it to the baseURL) with authorization headers */
  public async patch<T>(endpoint: Endpoint | string, body: any, config?: AxiosRequestConfig, internalConfig?: APIRequestConfig) {
    return this.processRequest(
      (await this.createPatchFactory<T>(endpoint, body, config, internalConfig))(),
      internalConfig
    )
  }

  /** Creates an API PUT endpoint call factory */
  public async createPutFactory<T>(endpoint: Endpoint | string, body: any, config?: AxiosRequestConfig, internalConfig?: APIRequestConfig) {
    return async () => axios.put<T>(`${internalConfig?.ignoreBaseUrl ? '' : this.baseUrl}${endpoint}`, body, internalConfig?.ignoreAuthorizationHeaders ? (config ?? {}) : await this.createConfigWithAuthorization(config))
  }

  /** Calls an API endpoint (appending it to the baseURL) with authorization headers */
  public async put<T>(endpoint: Endpoint | string, body: any, config?: AxiosRequestConfig, internalConfig?: APIRequestConfig) {
    return this.processRequest(
      (await this.createPutFactory<T>(endpoint, body, config, internalConfig))(),
      internalConfig
    )
  }

  /** Creates an API DELETE endpoint call factory */
  public async createDeleteFactory<T>(endpoint: Endpoint | string, config?: AxiosRequestConfig, internalConfig?: APIRequestConfig) {
    return async () => axios.delete<T>(`${internalConfig?.ignoreBaseUrl ? '' : this.baseUrl}${endpoint}`, internalConfig?.ignoreAuthorizationHeaders ? (config ?? {}) : await this.createConfigWithAuthorization(config))
  }

  /** Calls an API endpoint (appending it to the baseURL) with authorization headers */
  public async delete<T>(endpoint: Endpoint | string, config?: AxiosRequestConfig, internalConfig?: APIRequestConfig) {
    return this.processRequest(
      (await this.createDeleteFactory<T>(endpoint, config, internalConfig))(),
      internalConfig
    )
  }
}

// Lazy singleton: defer `new API(...)` until the first method call so that
// importing this module is side-effect free with respect to the runtime
// config singleton (`getApiUrl()` would throw if called before
// `loadRuntimeConfig()` resolves). The Proxy keeps the call shape
// `import API from 'utils/API/API'; API.get(...)` working unchanged.
let instance: API | null = null
const getInstance = (): API => {
  if (!instance) instance = new API(getApiUrl())
  return instance
}

const boundMethods = new Map<PropertyKey, (...args: unknown[]) => unknown>()

const lazyInstance = new Proxy({} as API, {
  get(_target, prop, _receiver) {
    // Short-circuit `then` so an accidental `await` / `Promise.resolve` on
    // the default export doesn't boot the API mid-import.
    if (prop === 'then') return undefined
    const inst = getInstance()
    const value = Reflect.get(inst, prop, inst)
    if (typeof value !== 'function') return value
    let bound = boundMethods.get(prop)
    if (!bound) {
      bound = (value as (...args: unknown[]) => unknown).bind(inst)
      boundMethods.set(prop, bound)
    }
    return bound
  },
})

export default lazyInstance
